import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop, hexToRGBA, getStrokeDasharray, isDefined } from "../../utils";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import { PriceCoordinate } from "../../coordinates";

class EachHorizontalLine extends Component {
  constructor(props) {
    super(props);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleHover = this.handleHover.bind(this);
    this.isHover = this.isHover.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.chartComponentRef = React.createRef();

    this.state = {
      hover: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.stroke !== this.props.stroke ||
      prevProps.strokeWidth !== this.props.strokeWidth ||
      prevProps.strokeOpacity !== this.props.strokeOpacity ||
      prevProps.strokeDasharray !== this.props.strokeDasharray
    ) {
      // Use ChartCanvas.redraw() to clear all canvases and redraw all subscriptions.
      // Accessed directly via this.context (contextTypes below) to avoid stale-ref issues
      // when shouldComponentUpdate on the child GenericChartComponent returns false.
      if (this.context?.redraw) {
        this.context.redraw();
      } else if (this.chartComponentRef.current) {
        // Fallback: redraw this component directly (may leave ghost until next clear)
        this.chartComponentRef.current.draw({ force: true });
      }
    }
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleDragStart() {
    const { yValue } = this.props;
    this.dragStart = {
      yValue,
    };
  }

  handleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const { yValue } = this.dragStart;

    const {
      chartConfig: { yScale },
      startPos,
      mouseXY,
    } = moreProps;

    const y = yScale(yValue);
    const dy = startPos[1] - mouseXY[1];

    const newYValue = yScale.invert(y - dy);

    onDrag(index, {
      yValue: newYValue,
    });
  }

  handleDragComplete(...rest) {
    this.dragStart = null;
    this.props.onDragComplete(...rest);
  }

  isHover(moreProps) {
    const { yValue, tolerance } = this.props;
    const {
      mouseXY,
      chartConfig: { yScale },
    } = moreProps;

    const lineY = yScale(yValue);
    const [, mouseY] = mouseXY;

    return Math.abs(mouseY - lineY) <= (tolerance || 7);
  }

  drawOnCanvas(ctx, moreProps) {
    const { yValue, stroke, strokeWidth, strokeOpacity, strokeDasharray } =
      this.props;
    const {
      chartConfig: { yScale },
      xScale,
    } = moreProps;

    const y = yScale(yValue);
    const range = xScale.range();
    const xStart = range[0];
    const xEnd = range[range.length - 1];

    ctx.save();
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);

    const dashSegments = getStrokeDasharray(strokeDasharray);
    if (dashSegments !== "none") {
      ctx.setLineDash(dashSegments.split(",").map((d) => Number(d.trim())));
    } else {
      ctx.setLineDash([]);
    }

    ctx.beginPath();
    ctx.moveTo(xStart, y);
    ctx.lineTo(xEnd, y);
    ctx.stroke();
    ctx.restore();
  }

  renderSVG(moreProps) {
    const { yValue, stroke, strokeWidth, strokeOpacity, strokeDasharray } =
      this.props;
    const {
      chartConfig: { yScale },
      xScale,
    } = moreProps;

    const y = yScale(yValue);
    const range = xScale.range();
    const xStart = range[0];
    const xEnd = range[range.length - 1];

    return (
      <line
        x1={xStart}
        y1={y}
        x2={xEnd}
        y2={y}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        strokeDasharray={getStrokeDasharray(strokeDasharray)}
      />
    );
  }

  render() {
    const {
      selected,
      hoverText,
      interactive,
      interactiveCursorClass,
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      displayFormat,
      coordinate,
      showCoordinate,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;
    const cursorClass =
      interactiveCursorClass || "react-stockcharts-move-cursor";

    return (
      <g>
        <GenericChartComponent
          ref={this.chartComponentRef}
          isHover={interactive !== false ? this.isHover : null}
          onHover={interactive !== false ? this.handleHover : null}
          onUnHover={interactive !== false ? this.handleHover : null}
          onDragStart={interactive !== false ? this.handleDragStart : null}
          onDrag={interactive !== false ? this.handleDrag : null}
          onDragComplete={
            interactive !== false ? this.handleDragComplete : null
          }
          svgDraw={this.renderSVG}
          canvasDraw={this.drawOnCanvas}
          canvasToDraw={getMouseCanvas}
          interactiveCursorClass={
            interactive !== false ? cursorClass : undefined
          }
          selected={selected}
          enableDragOnHover={interactive !== false}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />
        {showCoordinate !== false &&
          interactive !== false &&
          isDefined(displayFormat) && (
            <PriceCoordinate
              price={this.props.yValue}
              displayFormat={displayFormat}
              orient={coordinate?.orient || "right"}
              at={coordinate?.at || "right"}
              rectWidth={coordinate?.rectWidth || 60}
              rectHeight={coordinate?.rectHeight || 20}
              arrowWidth={coordinate?.arrowWidth || 0}
              dx={coordinate?.dx || 0}
              fill={coordinate?.fill || stroke}
              opacity={isDefined(coordinate?.opacity) ? coordinate.opacity : 1}
              textFill={coordinate?.textFill || "#FFFFFF"}
              fontFamily={
                coordinate?.fontFamily ||
                "Helvetica Neue, Helvetica, Arial, sans-serif"
              }
              fontSize={coordinate?.fontSize || 12}
              fontWeight={coordinate?.fontWeight || "normal"}
              stroke={coordinate?.stroke || stroke}
              strokeOpacity={
                isDefined(coordinate?.strokeOpacity)
                  ? coordinate.strokeOpacity
                  : strokeOpacity
              }
              strokeWidth={coordinate?.strokeWidth || strokeWidth}
              lineOpacity={coordinate?.lineOpacity || 0}
              lineStroke={coordinate?.lineStroke || stroke}
            />
          )}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && interactive !== false}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

EachHorizontalLine.propTypes = {
  index: PropTypes.number,
  yValue: PropTypes.any.isRequired,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeOpacity: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  selected: PropTypes.bool,
  hoverText: PropTypes.object.isRequired,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  interactive: PropTypes.bool,
  interactiveCursorClass: PropTypes.string,
  tolerance: PropTypes.number,
  displayFormat: PropTypes.func,
  coordinate: PropTypes.object,
  showCoordinate: PropTypes.bool,
};

EachHorizontalLine.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  interactive: true,
  selected: false,
  hoverText: {
    enable: false,
  },
  strokeDasharray: "Solid",
  strokeOpacity: 1,
  strokeWidth: 1,
  tolerance: 7,
  displayFormat: (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : String(value);
  },
  coordinate: {},
  showCoordinate: true,
};

EachHorizontalLine.contextTypes = {
  redraw: PropTypes.func,
};

export default EachHorizontalLine;
